import ErrorOutline from "@mui/icons-material/ErrorOutline";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { Box, Link, Stack, Tooltip, Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { StatusChip } from "components/StatusChip";
import { Issue, IssueLevel } from "generated-graphql/graphql";

import { containerTypeLabel } from "../Containers/containerDisplay";
import { DeadlineChip } from "./DeadlineChip";
import { isDisplayLine, StreamRollup } from "./shipmentContentsRow";
import { containerShipOutcome, formatQuantity, formatTotalsByUnit } from "./shipmentDisplay";

/**
 * A container's problems, stated on the row they belong to rather than in a
 * list the reader has to match back up to a container.
 */
export function ContainerIssues({ issues }: { issues: Issue[] }) {
  if (issues.length === 0) {
    return null;
  }
  const blocking = issues.some((issue) => issue.level === IssueLevel.Critical);
  const summary = issues.map((issue) => issue.message).join(" · ");
  return (
    /*
      Two separate things make this readable without a mouse, and the icon needs
      both. `titleAccess` clears SvgIcon's default `aria-hidden` and sets
      role="img", which is what exposes the message to a screen reader. `tabIndex`
      is what lets a sighted keyboard user reach the icon at all, since the
      tooltip opens on focus as well as hover — without it the message was
      pointer-only. A warning-level issue is stated nowhere else; critical ones
      also reach the page alert.
    */
    <Tooltip title={summary}>
      {blocking ? (
        <ErrorOutline
          color="error"
          titleAccess={summary}
          tabIndex={0}
          sx={{ fontSize: 16, flexShrink: 0 }}
        />
      ) : (
        <WarningAmber
          color="warning"
          titleAccess={summary}
          tabIndex={0}
          sx={{ fontSize: 16, flexShrink: 0 }}
        />
      )}
    </Tooltip>
  );
}

/** The row's amount, or the stream's per-unit total on a group row. */
export function AmountCell({
  params,
  rollups,
  shipped,
}: {
  params: GridRenderCellParams;
  rollups: Map<string, StreamRollup>;
  shipped: boolean;
}) {
  if (params.rowNode.type === "group") {
    const rollup = rollups.get(String(params.rowNode.groupingKey));
    if (!rollup) {
      return null;
    }
    return (
      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
        {formatTotalsByUnit(rollup.totals)}
      </Typography>
    );
  }
  const line = params.row;
  if (!isDisplayLine(line)) {
    return null;
  }
  const outcome = shipped ? containerShipOutcome(line) : null;
  return (
    <Box sx={{ minWidth: 0 }}>
      {/* Secondary to the group row's total above it: on a single-container
          stream the two are the same figure, and the heading owns the headline. */}
      <Typography
        variant="body2"
        color="text.secondary"
        noWrap
        sx={{ textDecoration: line.pending === "removed" ? "line-through" : undefined }}
      >
        {formatQuantity(line.quantity, line.unit)}
      </Typography>
      {outcome && (
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {outcome.label}
          {outcome.detail ? ` · ${outcome.detail}` : ""}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The container itself, over the facts that identify it.
 *
 * The label opens the container's drawer in place. A container IS a side-trip
 * from the shipment you are reading — which is the argument for a panel over
 * this page rather than the tab this used to open, since a side-trip you can
 * close is cheaper than one you have to navigate back from.
 */
export function ContainerCell({
  params,
  shipped,
  issuesForContainer,
  onOpenContainer,
}: {
  params: GridRenderCellParams;
  shipped: boolean;
  issuesForContainer: (containerId: string) => Issue[];
  onOpenContainer: (containerId: string) => void;
}) {
  const line = params.row;
  if (!isDisplayLine(line)) {
    return null;
  }
  const { container } = line;
  const issues = issuesForContainer(container.id);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        {/* Opens the container's drawer over this shipment rather than
            navigating (or opening a tab) to the containers list: the reader is
            checking what's on this shipment, and the answer should arrive
            without them losing the shipment to get it. */}
        <Link
          component="button"
          type="button"
          onClick={() => onOpenContainer(container.id)}
          variant="body2"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
            textAlign: "left",
          }}
        >
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {container.containerLabel}
          </Box>
        </Link>
        {/* The container's own problems, beside the container they are about —
            the classification columns are the stream's now, so this is where a
            per-container warning belongs. */}
        <ContainerIssues issues={issues} />
        {/* How long it has left on site, the same chip the packaging wizard and
            the add-containers dialog use — the deadline is what decides which
            drums go on a shipment, so the shipment should not be the one screen
            that drops it. Only while the waste is still here: once it has
            shipped the accumulation clock has stopped meaning anything, and a
            countdown would be answering a question nobody is asking. */}
        {!shipped && <DeadlineChip deadline={container.deadlineDate} />}
        {container.isPermanent && <StatusChip label="Tank" color="default" variant="soft" />}
        {line.pending === "added" && <StatusChip label="Adding" color="info" variant="soft" />}
        {line.pending === "removed" && (
          <StatusChip label="Removing" color="default" variant="soft" />
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" noWrap display="block">
        {[
          containerTypeLabel(container.containerType),
          // Pre-ship, what is in the container is the useful context; the
          // shipped quantity has its own column.
          shipped ? null : `${formatQuantity(container.amount, container.amountUnit)} on site`,
          container.accumulationArea?.name,
        ]
          .filter(Boolean)
          .join(" · ")}
      </Typography>
    </Box>
  );
}
